// components/DeliveryTable.jsx
import { useEffect, useState } from "react";
import {
  Table,
  Typography,
  Spin,
  Badge,
  Space,
  Input,
  Button,
  Row,
  Col,
} from "antd";

import { STATUS_CONFIG, STATUS_FILTERS } from "../helpers/Constants";

import AttemptTimeline from "../components/delivery/AttemptTimeline";
import {
  CopyableText,
  StatusPill,
  TrackingLink,
} from "../components/delivery/Atoms";
import InfoCards from "../components/delivery/Infocards";
import useDeliveryStore from "../store/DeliveryStore";
import {
  CloseCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { RecordsFilters } from "../components/delivery/RecordsFilters";

const { Text } = Typography;

// ─── Main Table ───────────────────────────────────────────────────────────
export default function DeliveryTable() {
  const {
    records,
    fetchRecords,
    loading,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
  } = useDeliveryStore();
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [inputValue, setInputValue] = useState(searchText);

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    setSearchText(inputValue);
  }, [inputValue]);

  const columns = [
    {
      title: "Order ID",
      dataIndex: "placed_order_id",
      width: 160,
      filters: [
        ...records.map((record) => ({
          text: record.placed_order_id,
          value: record.placed_order_id,
        })),
      ],
      filterSearch: true,
      onFilter: (value, record) =>
        `${record.placed_order_id}`.startsWith(value),
      render: (id, row) => (
        <div>
          <CopyableText
            style={{
              color: "#2563eb",
            }}
            text={id}
            showIcon={false}
          />
          <div style={{ marginTop: 4 }}>
            <Badge
              count={row.attempt_count}
              style={{
                background: "#f1f5f9",
                color: "#475569",
                fontSize: 12,
              }}
              title={`${row.attempt_count} attempt(s)`}
            />
            <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 6 }}>
              {row.attempt_count === 1 ? "Attempt" : "Attempts"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Latest Status",
      dataIndex: "latest_status",
      width: 150,
      filters: Array.from(
        new Map(
          records.map((record) => {
            const config =
              STATUS_CONFIG[record.latest_status] || STATUS_CONFIG.created;
            return [
              record.latest_status,
              { text: config.label, value: record.latest_status },
            ];
          }),
        ).values(),
      ),
      filterSearch: true,
      onFilter: (value, record) => record.latest_status.startsWith(value),
      render: (status) => <StatusPill status={status} />,
    },
    {
      title: "Outlet",
      dataIndex: "outlet",
      width: 130,
      render: (o) => <span>{o.name}</span>,
    },
    {
      title: "Latest Task ID",
      dataIndex: "latest_task_id",
      width: 200,
      render: (id) =>
        id ? (
          <CopyableText
            style={{
              color: "#3b82f6",
            }}
            text={id}
            truncate
          />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Tracking",
      dataIndex: "latest_tracking_link",
      width: 130,
      render: (link) => (
        <span onClick={(e) => e.stopPropagation()}>
          <TrackingLink href={link} />
        </span>
      ),
    },
    {
      title: "Rider",
      dataIndex: "latest_rider",
      width: 160,
      render: (_, row) =>
        row.latest_rider_name ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <CopyableText text={row.latest_rider_name} truncate />
            <CopyableText
              style={{
                color: "#3b82f6",
              }}
              text={row.latest_rider_phone}
              truncate
              linkType="phone_number"
            />
          </div>
        ) : (
          <Text type="secondary">Unassigned</Text>
        ),
    },
    {
      title: "Is Deleted?",
      dataIndex: "is_deleted",
      width: 120,
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {row.latest_cancellation_reason && (
            <Text style={{ color: "#94a3b8" }}>
              {row.latest_cancellation_reason}
            </Text>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <Text
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#0F172A",
              letterSpacing: "-0.3px",
              display: "block",
            }}
          >
            Delivery Records
          </Text>
          <Text
            style={{
              fontSize: 12,
              color: "#64748B",
              marginTop: 3,
              display: "block",
            }}
          >
            Grouped by order ID · click a row to expand
          </Text>
        </div>

        <Row justify="space-between" align="middle" style={{ width: "100%" }}>
          <Col>
            <Space size={8} wrap>
              <Input
                placeholder="Search order ID…"
                inputMode="numeric"
                pattern="[0-9]*"
                prefix={<SearchOutlined style={{ color: "#CBD5E1" }} />}
                value={inputValue}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/\D/g, "");
                  setInputValue(onlyNumbers);
                }}
                allowClear
                style={{
                  borderRadius: 8,
                  borderColor: "#E2E8F0",
                }}
              />
              {STATUS_FILTERS.map((f) => (
                <Button
                  key={f.value}
                  onClick={() => {
                    const finalFilter =
                      statusFilter === f.value ? "all" : f.value;
                    setStatusFilter(finalFilter);
                  }}
                  style={{
                    borderRadius: 8,
                    borderColor:
                      statusFilter === f.value ? "#BFDBFE" : "#E2E8F0",
                    background: statusFilter === f.value ? "#EFF6FF" : "#fff",
                    color: statusFilter === f.value ? "#1D4ED8" : "#64748B",
                    boxShadow: "none",
                    fontWeight: statusFilter === f.value ? 500 : 400,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {f.label}
                  {statusFilter !== "all" && statusFilter === f.value && (
                    <CloseCircleOutlined
                      style={{ marginRight: 0, lineHeight: 0 }}
                    />
                  )}
                </Button>
              ))}
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchRecords}
                loading={loading}
                style={{
                  borderRadius: 8,
                  borderColor: "#E2E8F0",
                  color: "#64748B",
                  boxShadow: "none",
                }}
              />
            </Space>
          </Col>
          <Col>
            <RecordsFilters />
          </Col>
        </Row>
      </div>
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Table
          className="delivery-table"
          rowKey="placed_order_id"
          columns={columns}
          dataSource={records}
          loading={{ spinning: loading, indicator: <Spin size="large" /> }}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpand: (expanded, record) => {
              setExpandedKeys(
                expanded
                  ? [...expandedKeys, record.placed_order_id]
                  : expandedKeys.filter((k) => k !== record.placed_order_id),
              );
            },
            expandedRowRender: (record) => (
              <div
                style={{
                  padding: "20px 20px 24px 20px",
                  background: "#F0F2F5",
                  borderTop: "1px solid #E2E8F0",
                }}
              >
                {!loading && (
                  <>
                    <InfoCards record={record} />
                    <AttemptTimeline order_id={record.placed_order_id} />
                  </>
                )}
              </div>
            ),
            rowExpandable: (record) => record.attempt_count > 0,
          }}
          scroll={{ x: 1000 }}
        />
      </div>
    </div>
  );
}
